import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Satellite, AlertTriangle, MapPin, ShieldCheck, Radio } from 'lucide-react';

// HeroProductVisual: A product-realistic composite showing a synthesised
// satellite-observation view of a hydropower project with an Aakash Drishti
// UI card overlay. The satellite view is composed as SVG (deterministic,
// on-brand, no random stock imagery) so it always represents the actual
// platform capability, not an unrelated photograph.
export function HeroProductVisual({ scrollY = 0 }) {
  const parallax = Math.min(scrollY * 0.03, 12);
  return (
    <div className="relative" data-testid="hero-product-visual">
      <div className="relative rounded-2xl overflow-hidden border border-border shadow-[0_20px_60px_-20px_rgba(11,27,46,0.35)]" style={{ transform: `translateY(-${parallax}px)` }}>
        {/* Base: synthesised satellite view */}
        <SatelliteScene />

        {/* corner markers */}
        <CornerMarker className="top-4 left-4" />
        <CornerMarker className="top-4 right-4" rotate="90" />
        <CornerMarker className="bottom-4 left-4" rotate="-90" />
        <CornerMarker className="bottom-4 right-4" rotate="180" />

        {/* observation source pill */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 rounded-full bg-background/85 backdrop-blur border border-border px-3 py-1 text-[11px] font-mono text-muted-foreground flex items-center gap-1.5">
          <Radio size={11} className="text-primary" /> LATEST OBSERVATION · 07:42 IST
        </div>

        {/* bottom overlay: alert card */}
        <div className="absolute bottom-4 left-4 right-4">
          <Card className="p-3.5 backdrop-blur-md bg-background/92 border-border/70 shadow-lg">
            <div className="flex items-start gap-3">
              <div className="rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-400 p-2">
                <AlertTriangle size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[13px] font-semibold">Probable landslide on slope above access road</div>
                  <Badge variant="outline" className="text-[9px] uppercase tracking-wider border-amber-500/40 text-amber-700 dark:text-amber-400">Simulated</Badge>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><MapPin size={10} /> 30.388°N, 78.472°E</span>
                  <span>1,372 m from asset</span>
                  <span>Evidence: <span className="font-medium text-foreground">Moderate</span></span>
                  <span>AI confidence: <span className="font-medium text-foreground">86%</span></span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="text-[10px]">Terrain disturbance</Badge>
                  <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-600 dark:text-red-400">Critical</Badge>
                  <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">Under Analyst Review</Badge>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Small stat card floating right */}
      <div className="hidden md:block absolute -bottom-6 -right-4 w-[220px]">
        <Card className="p-3.5 border-border shadow-lg bg-background">
          <div className="flex items-center gap-2">
            <div className="rounded-md bg-primary/10 text-primary p-1.5"><Satellite size={14} /></div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Observation freshness</div>
          </div>
          <div className="mt-2 font-display text-2xl font-semibold">21 seconds ago</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">Latest usable observation processed.</div>
        </Card>
      </div>
      <div className="hidden md:block absolute -top-6 -left-4 w-[220px]">
        <Card className="p-3.5 border-border shadow-lg bg-background">
          <div className="flex items-center gap-2">
            <div className="rounded-md bg-primary/10 text-primary p-1.5"><ShieldCheck size={14} /></div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Portfolio risk</div>
          </div>
          <div className="mt-2 font-display text-2xl font-semibold">64.3<span className="text-sm text-muted-foreground">/100</span></div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">3 monitored assets · Elevated</div>
        </Card>
      </div>
    </div>
  );
}

// SatelliteScene: A synthesised top-down view of a hydropower project.
// Shows reservoir, dam, powerhouse footprint, access road, terrain
// contours and monitoring zone polygons.
function SatelliteScene() {
  return (
    <svg
      viewBox="0 0 800 500"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-[440px] block"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label="Synthesised satellite-observation view of a hydropower project"
    >
      <defs>
        <linearGradient id="hero-terrain" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#254a3d" />
          <stop offset="55%" stopColor="#5a6b3e" />
          <stop offset="100%" stopColor="#3d4f2a" />
        </linearGradient>
        <linearGradient id="hero-water" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1e5a70" />
          <stop offset="100%" stopColor="#0f3348" />
        </linearGradient>
        <pattern id="contours" width="140" height="140" patternUnits="userSpaceOnUse">
          <path d="M0 60 Q35 30 70 55 T140 60" stroke="rgba(255,255,255,0.08)" fill="none" strokeWidth="1" />
          <path d="M0 90 Q35 60 70 85 T140 90" stroke="rgba(255,255,255,0.08)" fill="none" strokeWidth="1" />
          <path d="M0 120 Q35 90 70 115 T140 120" stroke="rgba(255,255,255,0.08)" fill="none" strokeWidth="1" />
        </pattern>
        <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
          <path d="M32 0 L0 0 0 32" fill="none" stroke="rgba(31,182,166,0.10)" strokeWidth="0.6" />
        </pattern>
      </defs>

      {/* Terrain base */}
      <rect width="800" height="500" fill="url(#hero-terrain)" />
      <rect width="800" height="500" fill="url(#contours)" />

      {/* Reservoir water body */}
      <path d="M120 90 Q220 60 340 75 Q470 90 540 140 Q580 180 560 235 Q510 275 400 275 Q310 275 220 250 Q140 225 110 175 Q90 130 120 90 Z" fill="url(#hero-water)" opacity="0.95" />
      <path d="M120 90 Q220 60 340 75 Q470 90 540 140 Q580 180 560 235 Q510 275 400 275 Q310 275 220 250 Q140 225 110 175 Q90 130 120 90 Z" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />

      {/* Dam structure */}
      <rect x="75" y="272" width="46" height="12" fill="#c7c9cd" />
      <rect x="75" y="282" width="46" height="3" fill="#8b8f95" />

      {/* Downstream river */}
      <path d="M75 285 Q60 320 68 360 Q78 400 60 440 Q40 470 45 500" stroke="#1e5a70" strokeWidth="7" fill="none" strokeLinecap="round" opacity="0.85" />

      {/* Powerhouse (small rectangle) */}
      <rect x="110" y="300" width="22" height="14" fill="#d0cfb0" stroke="#7a7a5a" strokeWidth="0.6" />

      {/* Access road (dashed light line) */}
      <path d="M50 470 Q130 430 200 400 Q280 370 340 340 Q400 310 460 290 Q510 275 555 260" stroke="rgba(255,255,255,0.6)" strokeWidth="2" fill="none" strokeDasharray="6 4" />

      {/* Grid overlay */}
      <rect width="800" height="500" fill="url(#grid)" />

      {/* Monitoring zones */}
      {/* Protection buffer around reservoir */}
      <path d="M100 75 Q220 40 340 55 Q490 75 560 130 Q615 180 585 245 Q535 300 400 300 Q290 300 210 275 Q120 250 90 190 Q65 130 100 75 Z" fill="none" stroke="rgba(31,182,166,0.55)" strokeWidth="1.4" strokeDasharray="3 3" />
      <text x="570" y="75" fontSize="11" fill="rgba(255,255,255,0.85)" fontFamily="monospace">PROTECTION BUFFER</text>

      {/* Sensitive slope zone (rectangle to upper right) */}
      <path d="M580 100 L720 110 L730 200 L590 190 Z" fill="rgba(255,178,0,0.10)" stroke="rgba(255,178,0,0.7)" strokeWidth="1.2" />
      <text x="595" y="128" fontSize="11" fill="rgba(255,178,0,0.95)" fontFamily="monospace">SENSITIVE SLOPE</text>

      {/* Restricted development zone */}
      <path d="M420 340 L540 350 L545 405 L425 400 Z" fill="rgba(239,68,68,0.10)" stroke="rgba(239,68,68,0.7)" strokeWidth="1.2" />
      <text x="440" y="367" fontSize="10" fill="rgba(255,255,255,0.9)" fontFamily="monospace">RESTRICTED ZONE</text>

      {/* Alert pin: probable landslide on slope */}
      <g transform="translate(660, 155)">
        <circle r="14" fill="rgba(255,178,0,0.28)" />
        <circle r="7" fill="#f59e0b" />
        <circle r="7" fill="none" stroke="#fff" strokeWidth="1.5" />
      </g>
      {/* Alert pin: reservoir spread */}
      <g transform="translate(320, 180)">
        <circle r="12" fill="rgba(31,182,166,0.30)" />
        <circle r="5" fill="#1FB6A6" />
      </g>
      {/* Alert pin: terrain on access road */}
      <g transform="translate(160, 425)">
        <circle r="12" fill="rgba(239,68,68,0.30)" />
        <circle r="5" fill="#ef4444" />
      </g>

      {/* Compass */}
      <g transform="translate(730, 440)" fill="rgba(255,255,255,0.9)" fontFamily="monospace" fontSize="9">
        <circle r="18" fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.6)" strokeWidth="0.8" />
        <path d="M0 -15 L4 4 L0 0 L-4 4 Z" fill="#fff" />
        <text x="-3" y="-20">N</text>
      </g>

      {/* Scale bar */}
      <g transform="translate(50, 465)" fontFamily="monospace" fontSize="9" fill="rgba(255,255,255,0.85)">
        <rect x="0" y="0" width="40" height="4" fill="#fff" />
        <rect x="40" y="0" width="40" height="4" fill="none" stroke="#fff" strokeWidth="1" />
        <text x="0" y="18">0</text>
        <text x="32" y="18">500m</text>
        <text x="70" y="18">1km</text>
      </g>

      {/* Labels */}
      <text x="330" y="170" fontSize="14" fill="rgba(255,255,255,0.85)" fontFamily="monospace" textAnchor="middle">RESERVOIR</text>
      <text x="55" y="268" fontSize="10" fill="rgba(255,255,255,0.85)" fontFamily="monospace">DAM</text>
    </svg>
  );
}

function CornerMarker({ className = '', rotate = '0' }) {
  return (
    <div className={`absolute h-5 w-5 ${className}`} style={{ transform: `rotate(${rotate}deg)` }}>
      <div className="absolute top-0 left-0 h-full w-[2px] bg-primary/70" />
      <div className="absolute top-0 left-0 h-[2px] w-full bg-primary/70" />
    </div>
  );
}
